import dayjs from "dayjs";
import { useRequestDependencies } from "entities/Request/api/hooks/useRequestDependencies";
import { useRequestStore } from "entities/Request/store/useRequestStore";
import { RequestAccessController } from "entities/Request/utils/RequestAccessController";
import {
  TravelLineForm,
  constants,
  travelLinesService,
  useRequestLinesStore,
} from "entities/TravelLines";
import { getTravelLineResolvers } from "features/EditRequestLines/lib/validations/getTravelLineResolvers";
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { useForm, useWatch } from "react-hook-form";

const access = new RequestAccessController();

const EditTravelLine = forwardRef(
  (
    {
      // @ts-ignore
      travelLineData,
      // @ts-ignore
      traveLineId,
      // @ts-ignore
      traveLineIndex,
      // @ts-ignore
      requestId,
      // @ts-ignore
      disabled,
      // @ts-ignore
      isLastLine,
    },
    ref
  ) => {
    const { defaultValues } = constants.travelLines;
    const request = useRequestStore((state) => state.request);

    const { data: { dependencies, validations } = {} } = useRequestDependencies(
      "travel_line_form",
      requestId,
      {
        triggerRequest: access.to("TravelLine", "edit").provided(),
      }
    );

    const traveIntervalTypes = dependencies?.allowed_interval_types.filter(
      (type) => type.label !== "Private time"
    );

    const favouritePlacesOptions = useMemo(() => {
      return [
        {
          label: "Favourite places",
          options: dependencies?.favorite_places.data.map((place) => ({
            value: place.id,
            label: place.name,
            country_code: place.country_code,
          })),
        },
      ];
    }, [dependencies]);

    const addRequestLineToStack = useRequestLinesStore(
      (state) => state.addRequestLineToStack
    );
    const updateRequestLinesState = useRequestLinesStore(
      (state) => state.updateRequestLinesState
    );
    const deleteRequestLineState = useRequestLinesStore(
      (state) => state.deleteRequestLineState
    );
    const travelPlanSchema = useRequestLinesStore(
      (state) => state.travelPlanSchema
    );
    const updateTravelPlanSchema = useRequestLinesStore(
      (state) => state.updateTravelPlanSchema
    );
    const removeTravelLine = useRequestLinesStore(
      (state) => state.removeTravelLine
    );

    const { onSaveResolver, onSubmitResolver } = getTravelLineResolvers();

    const resolver = useRef(onSaveResolver);

    const form = useForm({
      resolver: (...args) => resolver.current(...args),
      defaultValues,
    });

    const {
      handleSubmit,
      reset,
      control,
      setValue,
      getValues,
      resetField,
      formState: { isDirty, dirtyFields },
    } = form;

    const resetForm = () => {
      reset({
        ...Object.fromEntries(
          Object.entries(defaultValues).map(([key, value]) => {
            let newValue = travelLineData?.[key] ?? value;
            if (
              key === "start_time" ||
              key === "end_time" ||
              key === "border_crossing_time"
            ) {
              newValue = typeof newValue === "string" ? dayjs(newValue) : null;
            } else if (key === "attachments") {
              newValue = travelLineData?.[key]?.data?.map(
                (fileData) => fileData.id
              );
            }

            return [key, newValue];
          })
        ),
        id: traveLineId.toString().startsWith("initial") ? null : traveLineId,
        order: traveLineIndex,
      });
    };
    const [
      startPlace,
      endPlace,
      startTime,
      endTime,
      order,
      intervalType,
      vehicleInfo,
    ] = useWatch({
      control,
      name: [
        "start_place_id",
        "end_place_id",
        "start_time",
        "end_time",
        "order",
        "interval_type_id",
        "vehicle_info",
      ],
    });

    // ! Values of private car starts with "1_"

    const isPrivateCar = vehicleInfo?.value.toString().startsWith("1_");

    const isDomesticInterval =
      startPlace?.country_code === endPlace?.country_code;

    // International travel logic is not moved into separate hook to avoid conflicts in different branches at the time of writing the code
    // TODO Decomposition into entities/Request/hooks/useTravelPlan
    const isInternationalTravel = useMemo(() => {
      if (travelPlanSchema) {
        const travelLines = Object.values(travelPlanSchema);
        return travelLines.some((travelLine) => {
          return travelLines.some((line) => {
            let isInternationalStart = false,
              isInternationalEnd = false;

            if (line.startPlace && travelLine.startPlace)
              isInternationalStart =
                line.startPlace.country_code !==
                travelLine.startPlace.country_code;
            if (line.endPlace && travelLine.endPlace)
              isInternationalEnd =
                line.endPlace.country_code !== travelLine.endPlace.country_code;

            return isInternationalStart || isInternationalEnd;
          });
        });
      } else return false;
    }, [travelPlanSchema]);

    const showPlaceOfWorkSwitch = isInternationalTravel && !isLastLine;

    useEffect(() => {
      if (order !== "") {
        updateTravelPlanSchema({
          [order]: { startPlace, endPlace, startTime, endTime, intervalType },
        });
      }
    }, [startPlace, endPlace, startTime, endTime, order, intervalType]);

    useEffect(() => {
      if (vehicleInfo?.label === "Company car") {
        setValue("distance", null);
      }
    }, [vehicleInfo]);

    useEffect(() => {
      updateRequestLinesState(traveLineId, {
        isModified: isDirty || traveLineId.toString().startsWith("initial"),
      });
    }, [isDirty]);

    useEffect(() => {
      if (!showPlaceOfWorkSwitch)
        resetField("place_of_work", { defaultValue: false });
    }, [showPlaceOfWorkSwitch]);

    useEffect(() => {
      // ? Plane
      if (intervalType?.value === 3 && !isDomesticInterval) {
        if (request?.country_code === endPlace?.country_code) {
          return setValue("border_crossing_time", getValues("end_time"));
        }
        setValue("border_crossing_time", getValues("start_time"));
      }
    }, [
      intervalType,
      startTime,
      endTime,
      isDomesticInterval,
      request?.country_code,
      endPlace,
    ]);

    useEffect(() => {
      if (
        startTime &&
        !("end_time" in dirtyFields) &&
        !travelLineData.end_time
      ) {
        setValue("end_time", startTime);
      }
    }, [startTime]);

    useEffect(() => {
      setValue("order", traveLineIndex, { shouldDirty: true });
    }, [traveLineIndex]);

    useEffect(() => {
      resetForm();
    }, [travelLineData]);

    const handleRequestLineSave = (isDirty, traveLineId) => {
      addRequestLineToStack(
        new Promise((resolve) => {
          handleSubmit(
            (formData) =>
              resolve({
                isValid: true,
                formData: {
                  ...formData,
                  interval_type_id: formData.interval_type_id?.value,
                  vehicle_info: formData.vehicle_info?.value,
                  lineType: "travel",
                  isDirty:
                    isDirty || traveLineId.toString().startsWith("initial"),
                },
              }),
            (errors) => resolve({ isValid: false, errors })
          )();
        })
      );
    };

    const handleRequestLineDelete = async (traveLineId, requestId) => {
      if (traveLineId.toString().startsWith("initial")) {
        removeTravelLine(traveLineId);
        deleteRequestLineState(traveLineId);
      } else {
        updateRequestLinesState(traveLineId, { isDeleting: true });
        travelLinesService
          .deleteTravelLine(requestId, traveLineId)
          .then((response) => {
            if (response) {
              deleteRequestLineState(traveLineId);
              removeTravelLine(traveLineId);
            }
          });
      }
    };

    useImperativeHandle(ref, () => ({
      save() {
        resolver.current = onSaveResolver;
        handleRequestLineSave(isDirty, traveLineId);
      },
      submit() {
        resolver.current = onSubmitResolver;
        handleRequestLineSave(isDirty, traveLineId);
      },
      delete(requestId) {
        handleRequestLineDelete(traveLineId, requestId);
      },
    }));

    const uploadedFiles = useMemo(
      () =>
        travelLineData.attachments?.data.map((attachment) => ({
          ...attachment,
          isUploaded: true,
        })),
      [travelLineData.attachments?.data]
    );

    return (
      <TravelLineForm
        form={form}
        disabled={disabled}
        favouritePlacesOptions={favouritePlacesOptions}
        uploadedFiles={uploadedFiles}
        validations={validations}
        handleSubmit={handleSubmit}
        traveIntervalTypes={traveIntervalTypes}
        vehicleOptions={dependencies?.vehicles}
        intervalType={intervalType}
        isPrivateCar={isPrivateCar}
        isDomesticInterval={isDomesticInterval}
        placeOfWorkIsAllowed={
          request?.is_place_of_work_allowed && showPlaceOfWorkSwitch
        }
      />
    );
  }
);

export default memo(EditTravelLine);
